import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { checkProjectDomain } from '../../services/projectDomain';
import { slugify } from '../../utils/slug';
import { sanitizeChange } from '../../utils/sanitizeInput';

const DOMAIN_CHECK_DEBOUNCE_MS = 450;

interface DomainStatus {
  kind: 'idle' | 'checking' | 'available' | 'taken' | 'error';
  msg?: string;
}

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  error: string;
  name: string;
  setName: (v: string) => void;
  clientName: string;
  setClientName: (v: string) => void;
  customDomain?: string;
  setCustomDomain: (v: string) => void;
  projectType?: number;
  promptVersion?: number | null;
  projectId?: string;
}

export const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  error,
  name,
  setName,
  clientName,
  setClientName,
  customDomain,
  setCustomDomain,
  projectType,
  promptVersion,
  projectId,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [initialDomain, setInitialDomain] = useState<string | undefined>('');

  useEffect(() => {
    if (isOpen) setInitialDomain(customDomain);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [domainStatus, setDomainStatus] = useState<DomainStatus>({ kind: 'idle' });
  // Drops responses that no longer describe the current value.
  const checkSeq = useRef(0);

  const normalizedCurrent = customDomain?.trim() || '';
  const normalizedInitial = initialDomain?.trim() || '';
  const isInitialDomain = normalizedCurrent === normalizedInitial;

  // Checked as the user types, debounced. The project's own current domain is
  // not a candidate — no check, no verdict, until the value actually changes.
  const domainCheckEnabled = isOpen && !!normalizedCurrent && !isInitialDomain;

  useEffect(() => {
    checkSeq.current++;
    if (!domainCheckEnabled) {
      setDomainStatus({ kind: 'idle' });
      return;
    }
    setDomainStatus({ kind: 'checking' });
    const id = checkSeq.current;
    const timer = setTimeout(async () => {
      try {
        const result = await checkProjectDomain(normalizedCurrent, projectId);
        if (id !== checkSeq.current) return;
        setDomainStatus(
          result.available
            ? { kind: 'available' }
            : { kind: 'taken', msg: result.reason || 'Domain is unavailable' }
        );
      } catch {
        if (id !== checkSeq.current) return;
        setDomainStatus({ kind: 'error', msg: "Couldn't reach the server. It'll retry as you type." });
      }
    }, DOMAIN_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedCurrent, domainCheckEnabled]);

  const isSubmitDisabled = loading || !name.trim() || (!isInitialDomain && !!normalizedCurrent && domainStatus.kind !== 'available');

  const domainBad = domainStatus.kind === 'taken' || domainStatus.kind === 'error';
  const domainMessage =
    domainStatus.msg ||
    (domainStatus.kind === 'checking'
      ? 'Checking availability…'
      : domainStatus.kind === 'available'
      ? 'Domain is available.'
      : '');
  const domainIcon =
    domainStatus.kind === 'checking' ? (
      <Icon name={IconName.Loading} size="sm" spin className="text-text-tertiary" inheritColor />
    ) : domainStatus.kind === 'available' ? (
      <Icon name={IconName.Check} size="sm" variant="success" />
    ) : domainBad ? (
      <Icon name={IconName.Close} size="sm" variant="error" />
    ) : undefined;

  // Shared with the environment form's name and domain fields: one rule for
  // what a URL-safe label may contain, and one helper that keeps the caret
  // where the user left it. Sanitizing on every keystroke rewrites the whole
  // value, which otherwise throws the cursor to the end of the field every
  // time someone types a space, an underscore or a capital.
  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDomain(sanitizeChange(e, slugify));
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Edit Project</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={loading}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto">
            <div className="w-full space-y-6">
              <Input
                id="project-name-edit"
                name="project-name-edit"
                label="Project Name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                autoFocus
                placeholder="Enter project name"
                className="w-full"
              />
              <Input
                id="client-name-edit"
                name="client-name-edit"
                label="Client Name"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder="Enter client name"
                className="w-full"
              />
              
              {projectType === 1 && (promptVersion || 0) >= 2 && (
                <div className="flex-1 min-w-[280px]">
                  <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Custom Domain</Text>
                  <Input
                    id="custom-domain-edit"
                    name="custom-domain-edit"
                    value={customDomain || ''}
                    onChange={handleDomainChange}
                    autoComplete="off"
                    spellCheck={false}
                    variant={domainStatus.kind === 'available' ? 'success' : domainBad ? 'error' : 'default'}
                    endIcon={domainIcon}
                    suffix=".veyloapp.ai"
                  />
                  {domainMessage && (
                    <Text
                      variant="text-xs"
                      color={domainBad ? 'error' : domainStatus.kind === 'available' ? 'success' : 'tertiary'}
                      className="mt-bg-v2-xs break-words block"
                    >
                      {domainMessage}
                    </Text>
                  )}
                </div>
              )}
            </div>

            {error && <Text variant="text-sm" color="error" className="w-full">{error}</Text>}
          </div>

          {/* Last Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={loading}
            />
            <Button
              variant="primary"
              type="submit"
              label={loading ? 'Saving...' : 'Save Changes'}
              disabled={isSubmitDisabled}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

